import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ImagePlus, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate, TEAM_LABELS } from "../../lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

const EMPTY_RIDER = { nom: "", prenom: "", place: "", temps: "", ecart: "" };

const EMPTY_RESULT = {
  date: "", name: "", location: "", team: "feminine",
  coureurs: [], resultat_equipe: "", a_retenir: "", photo: "", lien_classement: "", statut: "brouillon",
};

export default function AdminResultats() {
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RESULT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/results");
      setResults(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY_RESULT); window.scrollTo({ top: 0 }); };
  const startEdit = (r) => {
    setEditing(r.id);
    setForm({ ...EMPTY_RESULT, ...Object.fromEntries(Object.keys(EMPTY_RESULT).map((k) => [k, r[k] ?? EMPTY_RESULT[k]])) });
    window.scrollTo({ top: 0 });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setRider = (i, k, v) => setForm({ ...form, coureurs: form.coureurs.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const addRider = () => setForm({ ...form, coureurs: [...form.coureurs, { ...EMPTY_RIDER }] });
  const removeRider = (i) => setForm({ ...form, coureurs: form.coureurs.filter((_, j) => j !== i) });

  const save = async (e, statut) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, statut: statut || form.statut, coureurs: form.coureurs.filter((c) => c.nom.trim() && c.place.trim()) };
      if (editing === "new") {
        await api.post("/admin/results", payload);
        toast.success(statut === "publie" ? "Résultat publié" : "Brouillon enregistré");
      } else {
        await api.put(`/admin/results/${editing}`, payload);
        toast.success(statut === "publie" ? "Résultat publié" : "Résultat mis à jour");
      }
      setEditing(null);
      setForm(EMPTY_RESULT);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (r) => {
    try {
      const { coureurs, ...rest } = r;
      await api.put(`/admin/results/${r.id}`, { ...rest, coureurs: coureurs || [], statut: r.statut === "publie" ? "brouillon" : "publie" });
      toast.success(r.statut === "publie" ? "Résultat dépublié" : "Résultat publié");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer le résultat « ${r.name} » ?`)) return;
    try {
      await api.delete(`/admin/results/${r.id}`);
      toast.success("Résultat supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd);
      setForm((f) => ({ ...f, photo: data.path }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <p className="text-steel text-sm" data-testid="results-count">{results.length} résultat{results.length > 1 ? "s" : ""}</p>
        <button onClick={startNew} data-testid="result-new" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3 hover:bg-navy transition-colors duration-300">
          <Plus size={16} /> Ajouter un résultat
        </button>
      </div>

      {editing && (
        <form onSubmit={(e) => save(e)} data-testid="result-editor" className="bg-white border border-navy/10 p-6 sm:p-8 mb-10 space-y-5">
          <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">
            {editing === "new" ? "Ajouter un résultat" : "Modifier le résultat"}
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><label className={labelCls}>Nom de la course *</label><input data-testid="result-name" required value={form.name} onChange={set("name")} className={inputCls} /></div>
            <div><label className={labelCls}>Date *</label><input data-testid="result-date" type="date" required value={form.date} onChange={set("date")} className={inputCls} /></div>
            <div><label className={labelCls}>Lieu</label><input data-testid="result-location" value={form.location} onChange={set("location")} className={inputCls} /></div>
            <div>
              <label className={labelCls}>Équipe / groupe</label>
              <select data-testid="result-team" value={form.team} onChange={set("team")} className={inputCls}>
                {Object.entries(TEAM_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Résultat par équipe (facultatif)</label><input data-testid="result-team-result" value={form.resultat_equipe} onChange={set("resultat_equipe")} className={inputCls} placeholder="2e" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls + " mb-0"}>Résultats individuels</label>
              <button type="button" onClick={addRider} data-testid="rider-add" className="inline-flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider text-rouge hover:text-navy transition-colors">
                <Plus size={14} /> Ajouter un coureur
              </button>
            </div>
            {form.coureurs.length === 0 && <p className="text-xs text-steel">Aucun coureur — cliquez « Ajouter un coureur ».</p>}
            <div className="space-y-3">
              {form.coureurs.map((c, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_90px_1fr_1fr_40px] gap-3 items-end bg-mist p-3" data-testid={`rider-row-${i}`}>
                  <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Prénom</label><input data-testid={`rider-prenom-${i}`} value={c.prenom} onChange={(e) => setRider(i, "prenom", e.target.value)} className={inputCls} /></div>
                  <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Nom *</label><input data-testid={`rider-nom-${i}`} value={c.nom} onChange={(e) => setRider(i, "nom", e.target.value)} className={inputCls} /></div>
                  <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Place *</label><input data-testid={`rider-place-${i}`} value={c.place} onChange={(e) => setRider(i, "place", e.target.value)} className={inputCls} placeholder="1re" /></div>
                  <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Temps</label><input data-testid={`rider-temps-${i}`} value={c.temps} onChange={(e) => setRider(i, "temps", e.target.value)} className={inputCls} /></div>
                  <div><label className="block text-[10px] uppercase tracking-wider text-steel mb-1">Écart</label><input data-testid={`rider-ecart-${i}`} value={c.ecart} onChange={(e) => setRider(i, "ecart", e.target.value)} className={inputCls} /></div>
                  <button type="button" onClick={() => removeRider(i)} data-testid={`rider-remove-${i}`} className="p-2.5 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer ce coureur"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div><label className={labelCls}>À retenir (facultatif)</label><textarea data-testid="result-highlight" rows={3} value={form.a_retenir} onChange={set("a_retenir")} className={inputCls} placeholder="Une belle performance de l'équipe avec…" /></div>
          <div><label className={labelCls}>Lien vers le classement complet (facultatif)</label><input data-testid="result-link" type="url" value={form.lien_classement} onChange={set("lien_classement")} className={inputCls} placeholder="https://…" /></div>

          <div>
            <span className={labelCls}>Photo de la course (facultative)</span>
            {form.photo ? (
              <div className="flex items-center gap-4" data-testid="result-photo-preview">
                <img src={`${API_BASE}/api/files/${form.photo}`} alt="Aperçu" className="h-24 w-40 object-cover border border-navy/10" />
                <button type="button" onClick={() => setForm({ ...form, photo: "" })} data-testid="result-photo-remove" className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                  <X size={14} /> Retirer
                </button>
              </div>
            ) : (
              <label data-testid="result-photo-upload" className="flex items-center justify-center gap-3 border border-dashed border-navy/25 px-6 py-8 cursor-pointer text-steel text-sm hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                {uploading ? "Téléversement…" : "Choisir une image (jpg, png, webp — 8 Mo max)"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" disabled={uploading} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
              </label>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="submit" disabled={saving || uploading} data-testid="result-save-draft" className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge disabled:opacity-60 transition-[border-color,color] duration-300">
              {saving && <Loader2 size={15} className="animate-spin" />}
              {editing === "new" ? "Enregistrer en brouillon" : "Enregistrer"}
            </button>
            <button type="button" onClick={(e) => save(e, "publie")} disabled={saving || uploading} data-testid="result-save-publish" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
              Publier
            </button>
            <button type="button" onClick={() => setEditing(null)} data-testid="result-cancel" className="border border-navy/15 text-steel font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
              Annuler
            </button>
          </div>
        </form>
      )}

      {busy ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
      ) : results.length === 0 ? (
        <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="results-empty-admin">
          <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucun résultat</p>
          <p className="mt-2 text-sm text-steel">Ajoutez le premier résultat du club avec le bouton « Ajouter un résultat ».</p>
        </div>
      ) : (
        <div className="bg-white border border-navy/10" data-testid="results-table-admin">
          {results.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-navy/10 last:border-0" data-testid={`result-row-${r.id.slice(0, 8)}`}>
              <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${r.statut === "publie" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid={`result-statut-${r.id.slice(0, 8)}`}>
                {r.statut === "publie" ? "Publié" : "Brouillon"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading uppercase font-semibold text-navy truncate">{r.name}</p>
                <p className="text-xs text-steel">{fmtDate(r.date)} · {r.location || "—"} · {TEAM_LABELS[r.team] || r.team} · {r.coureurs?.length || 0} coureur(s)</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => togglePublish(r)} data-testid={`result-toggle-${r.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" title={r.statut === "publie" ? "Dépublier" : "Publier"} aria-label="Publier / dépublier">
                  {r.statut === "publie" ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button onClick={() => startEdit(r)} data-testid={`result-edit-${r.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Modifier"><Pencil size={16} /></button>
                <button onClick={() => remove(r)} data-testid={`result-delete-${r.id.slice(0, 8)}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
